import Downtime from '@components/layout/downtime';
import type { Integration } from '@interfaces/integrations';
import {
  IntegrationProviders,
  IntegrationState,
  IntegrationTypes,
} from '@interfaces/integrations';
import { PageConfigurationTypes } from '@interfaces/pageConfiguration';
import { NavigationStyles } from '@interfaces/role';
import { BootSpinner } from '@ovComponents/1-primative/bootSpinner/bootSpinner';
import { AppBar, Toolbar } from '@ovComponents/2-component/appBar/appBar';

import { AppBreadcrumbs } from '@ovComponents/3-pattern/appBreadcrumbs/appBreadcrumbs';
import EntityMenu from '@ovComponents/3-pattern/entityMenu/entityMenu';
import ErrorBoundary from '@ovComponents/3-pattern/errorBoundary/errorBoundary';
import { useAuthContext } from '@providers/ovApolloProvider';
import {
  UserContext,
  useFeatureFlag,
  usePermission,
} from '@providers/userContextProvider';
import { ACTIVE_ORGANIZATION_QUERY_PARAM } from '@utils/activeOrganization';
import { entityName } from '@utils/common';
import { FlagsmithFlags } from '@utils/flagsmith';
import { Color } from 'hephaestus/core';
import { Box, IconButton, useSnackbar, useTheme } from 'hephaestus/web';
import { Menu as MenuIcon } from 'hephaestus/webIcons';
import { isUndefined } from 'lodash-es';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Notifications } from '../notifications/notifications';
import { getZendeskKeys, ZendeskWidget } from '../widgets/chatAgent/zendesk';
import AlertBanner from './alertBanner';
import Disclaimer from './disclaimer';
import { useLayoutTokens } from './layout.tokens';
import OrganizationLogo from './organizationLogo';
import Sidebar from './sidebar';

const pageTypeMap: Record<string, PageConfigurationTypes> = {
  clients: PageConfigurationTypes.INDIVIDUAL,
  nonIndividualClients: PageConfigurationTypes.NON_INDIVIDUAL,
  households: PageConfigurationTypes.HOUSEHOLD,
  household: PageConfigurationTypes.HOUSEHOLD,
};

const pageTypes = [
  PageConfigurationTypes.INDIVIDUAL,
  PageConfigurationTypes.NON_INDIVIDUAL,
  PageConfigurationTypes.HOUSEHOLD,
];

const ALERT_BANNER_IGNORE_PATHS: readonly string[] = [
  'settings',
  'reconciliation',
  'orgSettings',
];

// read query params once per path (memoized) to avoid drift across effects
const useQueryParams = () =>
  useMemo(() => new URLSearchParams(window.location.search), []);

const Layout = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const {
    userContext,
    displayMenuBar,
    closed,
    integrations,
    // ^^ is needed to conditionally load 3rd party integration scripts
    // e.g for Zendesk and Fraud Detection (Simility)
    applicableDowntimeSchedule,
    organizationLoading,
    organizationFetchFailed,
  } = useContext(UserContext);
  const canReadNotification = usePermission('read:notification');
  const alertBannersEnabled = useFeatureFlag(FlagsmithFlags.alert_banners);
  const { appLogout } = useAuthContext();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const theme = useTheme();
  const layoutTokens = useLayoutTokens(theme);

  const logout = async () => {
    await appLogout();
  };

  // This it to make pages scroll to top after every navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // RouterLink appends the active org id so it survives opening a link in a new
  // tab. For same-tab navigations the param is redundant (session already
  // holds it), so strip it here to keep the address bar clean.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has(ACTIVE_ORGANIZATION_QUERY_PARAM)) return;

    params.delete(ACTIVE_ORGANIZATION_QUERY_PARAM);
    const search = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : '',
        hash: location.hash,
      },
      { replace: true },
    );
  }, [location, navigate]);

  // read query once to prevent subtle differences
  const query = useQueryParams();

  // This it to redirect to root path on downtime
  useEffect(() => {
    if (applicableDowntimeSchedule) navigate('/');
  }, [navigate, applicableDowntimeSchedule]);

  const splitPath = pathname.split('/').filter(Boolean);
  const pageTypeFromPath = pageTypeMap[splitPath[splitPath.length - 2]];
  const hasNotifications = canReadNotification;
  const isUnifiedClientExperience =
    userContext.role?.navigationStyle === NavigationStyles.SIMPLE;
  const appBarMobileAndTabletColor = layoutTokens.backgroundColor;
  const appBarDesktopColor = layoutTokens.backgroundColor;
  const hideAppBarActions = !!applicableDowntimeSchedule;

  // conditionally load the Simility fraud detection scripts
  useEffect(() => {
    const conditionallyInjectSimilityScripts = (
      orgIntegrations: Integration[],
    ) => {
      const config = orgIntegrations.find(
        ({ type, provider, state }) =>
          type === IntegrationTypes.FRAUD_DETECTION &&
          provider === IntegrationProviders.SIMILITY &&
          state === IntegrationState.ACTIVE,
      )?.configuration;

      if (
        !config ||
        !config.pageKeys ||
        !config.fnclsValue ||
        !config.scriptInjectURL
      )
        return;

      const CMID = window.sessionStorage.getItem('CMID');
      if (!CMID) {
        console.warn(
          'Missing CMID. Will abort Simility fraud management initialization for ORG client!',
        );
        return;
      }

      const { scriptInjectURL, fnclsValue, pageKeys = '' } = config;
      const pageKeysList = pageKeys.split(/\s*,\s*/);

      const page = pageKeysList.find((entry: string) => {
        const match = pathname.match(/\/+([a-zA-Z]+)\/?.*/);
        return match?.at(1) === entry;
      });

      if (!page) return;

      // add/update script data values for this path
      let dataIslandScript = document.querySelector('script[fncls]');
      if (!dataIslandScript) {
        dataIslandScript = document.createElement('script');
        dataIslandScript.setAttribute('type', 'application/json');
        dataIslandScript.setAttribute('fncls', fnclsValue);
        try {
          document.body.appendChild(dataIslandScript);
        } catch (err) {
          console.error('Error injecting fraud detection data script:', err);
        }
      }

      dataIslandScript.textContent = JSON.stringify({
        f: CMID,
        s: page.toUpperCase(),
      });

      // add Simility script
      let fraudnetInitScript = document.querySelector('#fraudnet-init');
      if (!fraudnetInitScript) {
        fraudnetInitScript = document.createElement('script');
        fraudnetInitScript.setAttribute('id', 'fraudnet-init');
        fraudnetInitScript.setAttribute('src', scriptInjectURL);
        try {
          document.body.appendChild(fraudnetInitScript);
        } catch (err) {
          console.error('Error injecting fraud detection init script:', err);
        }
      }
    };

    if (userContext) {
      conditionallyInjectSimilityScripts(integrations);
    }
  }, [pathname, userContext, integrations]);

  // handle `custodianmeta` redirects into OV — currently only the credentials
  // manager success toast via traceId handshake. The logout-on-redirect cases
  // (status=LOGOUT, loggedOutMessage=inactive|changeusername) are handled in an
  // earlier layer in Auth0Provider's mount-effect pre-flight. (OVT-5794)
  useEffect(() => {
    const custodianMeta = query.get('custodianmeta') || '';
    if (!custodianMeta) return;

    const traceId = custodianMeta.match(/traceId:([^&]+)/)?.[1];
    if (!traceId) return;

    const storedTraceId = window.localStorage.getItem(
      'custodianCredentialsManagerTraceId',
    );
    if (traceId !== storedTraceId) return;

    window.localStorage.removeItem('custodianCredentialsManagerTraceId');
    // UX: this is the "settings updated" case. Can be updated when other cases arise
    enqueueSnackbar('Settings updated successfully', { variant: 'success' });
  }, []);

  const pageOptions = () => {
    switch (pageTypeFromPath) {
      case PageConfigurationTypes.INDIVIDUAL:
        return userContext?.role?.individualPageConfiguration?.options;
      case PageConfigurationTypes.NON_INDIVIDUAL:
        return userContext?.role?.nonIndividualPageConfiguration?.options;
      case PageConfigurationTypes.HOUSEHOLD:
        return userContext?.role?.householdPageConfiguration?.options;
      default:
        return undefined;
    }
  };

  const options = pageOptions();

  return (
    <Box sx={{ display: 'flex' }}>
      {displayMenuBar && (
        <AppBar
          position='fixed'
          elevation={0}
          sx={{
            width: { md: '100%' },
            height: 72,
            // Drawer default zIndex (1200) > AppBar (1100); raise so bar
            // shadow casts onto side nav instead of sitting under it.
            zIndex: (t) => t.zIndex.drawer + 1,
            transition: 'all 0.3s',
            backgroundColor: {
              xs: appBarMobileAndTabletColor,
              md: appBarDesktopColor,
            },
            // Figma Top Bar (827:20839): 0 0 14px 5% black
            boxShadow: '0px 0px 14px 0px rgba(0, 0, 0, 0.05)',
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              boxSizing: 'border-box',
              // Figma Top Bar: 72 tall, 20px horizontal. Vertical pad via
              // flex center — do not add py (height+py stacked past 72).
              height: 72,
              minHeight: 72,
              maxHeight: 72,
              px: '20px',
              py: 0,
              '@media (min-width: 0px)': {
                minHeight: 72,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {!hideAppBarActions && (
                <IconButton
                  aria-label='menu'
                  onClick={() => setMobileOpen(!mobileOpen)}
                  size='small'
                  sx={{
                    mr: 1,
                    color: layoutTokens.textColor,
                    display: {
                      xs: !isUnifiedClientExperience ? 'inline-flex' : 'none',
                      lg: 'none',
                    },
                    '&:hover': {
                      backgroundColor: Color(layoutTokens.textColor)
                        .alpha(0.08)
                        .string(),
                    },
                    '&:active': {
                      backgroundColor: Color(layoutTokens.textColor)
                        .alpha(0.16)
                        .string(),
                    },
                    '&:focus-visible': {
                      backgroundColor: Color(layoutTokens.textColor)
                        .alpha(0.16)
                        .string(),
                    },
                  }}
                >
                  <MenuIcon fontSize='inherit' />
                </IconButton>
              )}
              <OrganizationLogo textColor={layoutTokens.textColor} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {hasNotifications && !hideAppBarActions && (
                <Notifications color={layoutTokens.textColor} />
              )}
              <EntityMenu
                logout={logout}
                showOnlyLogout={hideAppBarActions}
                color={layoutTokens.textColor}
              />
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: '100%' },
          maxWidth: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)' },
          bgcolor: theme.color.background,
          mt: isUnifiedClientExperience && !displayMenuBar ? 0 : '72px',
        }}
      >
        {organizationFetchFailed ? (
          <ErrorBoundary />
        ) : organizationLoading ? (
          <BootSpinner />
        ) : applicableDowntimeSchedule ? (
          <>
            {!displayMenuBar && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: isUnifiedClientExperience ? 'center' : 'start',
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    maxWidth: '1080px',
                    width: '100%',
                    height: '32px',
                    mb: 2,
                    ml: { xs: 0, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'end',
                      alignItems: 'center',
                      marginLeft: 'auto',
                    }}
                  >
                    <EntityMenu
                      logout={logout}
                      showOnlyLogout
                      color={layoutTokens.textColor}
                    />
                  </Box>
                </Box>
              </Box>
            )}
            <Downtime downtime={applicableDowntimeSchedule} />
          </>
        ) : (
          <Box
            sx={{
              justifyContent: 'start',
              alignItems: 'center',
              display: 'flex',
              height: '100%',
            }}
          >
            {!isUnifiedClientExperience && (
              <Sidebar
                drawerWidth={closed ? 48 : 240}
                setMobileOpen={setMobileOpen}
                mobileOpen={mobileOpen}
              />
            )}
            <Box
              sx={{
                width: '100%',
                transition: 'all 0.4s',

                maxWidth: {
                  lg: `calc(100% - ${!isUnifiedClientExperience ? `${closed ? 48 : 240}px` : '0px'})`,
                },

                ml: {
                  xs: '0px',
                  sm: '0px',
                  md: '0px',
                  lg: !isUnifiedClientExperience
                    ? `${closed ? 48 : 240}px`
                    : '0px',
                },
              }}
            >
              {alertBannersEnabled &&
                !ALERT_BANNER_IGNORE_PATHS.includes(splitPath[0]) && (
                  <AlertBanner />
                )}
              {pageTypes.includes(pageTypeFromPath) &&
                options?.enableContent && (
                  <Disclaimer
                    options={options}
                    sx={{
                      maxWidth: !isUnifiedClientExperience
                        ? `${options?.maxPageWidth || 1080}px`
                        : '100%',
                    }}
                  />
                )}
              {/* Note: The breadcrumb for "UnifiedClientExperience" when "displayMenuBar" is set to true is displayed on pages such as viewPageVisual */}
              {!isUnifiedClientExperience && displayMenuBar && (
                <Box
                  sx={{
                    mb: 3,
                    mt: 1,
                  }}
                >
                  <AppBreadcrumbs />
                </Box>
              )}
              {!displayMenuBar && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignContent: isUnifiedClientExperience
                      ? 'center'
                      : 'start',
                    flexWrap: 'wrap',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      maxWidth: '1080px',
                      width: '100%',
                      height: '32px',
                      mb: 2,
                      ml: { xs: 0, sm: 2 },
                    }}
                  >
                    <AppBreadcrumbs />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'end',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        gap: '4px',
                      }}
                    >
                      {hasNotifications && (
                        <Notifications color={layoutTokens.textColor} />
                      )}
                      <EntityMenu
                        logout={logout}
                        color={layoutTokens.textColor}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
              {/* This is where actual component gets rendered (see appRoutes.tsx) */}
              <Outlet />
              {/* Temporary Zendesk workaround. Refactor when adding new chat providers. */}
              {userContext?.accessType &&
                !isUndefined(getZendeskKeys(integrations)) && (
                  <ZendeskWidget
                    widgetStyle={{ color: theme.color.primary }}
                    accessType={userContext?.accessType}
                    user={entityName(userContext)}
                    scriptKeys={getZendeskKeys(integrations)}
                  />
                )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Layout;
